import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseTokenValidationReturn {
  validateToken: (code: string) => Promise<{ valid: boolean; reason?: string }>;
  redeemToken: (code: string, sessionId: string, email: string, sessionSecret: string) => Promise<{ success: boolean; tokenId?: string; error?: string }>;
  completeWithToken: (sessionId: string, sessionSecret: string) => Promise<{ success: boolean; error?: string }>;
  validating: boolean;
  redeeming: boolean;
}

export function useTokenValidation(): UseTokenValidationReturn {
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const validateToken = async (code: string): Promise<{ valid: boolean; reason?: string }> => {
    setValidating(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-token", {
        body: { code },
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Token validation error:", err);
      return { valid: false, reason: "Validation failed" };
    } finally {
      setValidating(false);
    }
  };

  const redeemToken = async (
    code: string,
    assessmentId: string,
    email: string,
    sessionSecret: string
  ): Promise<{ success: boolean; tokenId?: string; error?: string }> => {
    setRedeeming(true);
    try {
      const { data, error } = await supabase.functions.invoke("redeem-token", {
        body: { code, assessmentId, email, sessionSecret },
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Token redemption error:", err);
      return { success: false, error: "Redemption failed" };
    } finally {
      setRedeeming(false);
    }
  };

  const completeWithToken = async (
    sessionId: string,
    sessionSecret: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("complete-session", {
        body: { sessionId, sessionSecret },
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Session completion error:", err);
      return { success: false, error: "Completion failed" };
    }
  };

  return {
    validateToken,
    redeemToken,
    completeWithToken,
    validating,
    redeeming,
  };
}
